import {Injectable} from '@angular/core';
import {BaseService} from '@app/core/services/base.service';
import {AuthService} from '@app/core/services/auth.service';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {HttpService} from '@app/core/services/http.service';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {Shift} from '@app/core/models/shift.model';
import {Moment} from 'moment';
import {RDate} from '@app/core/models/rDate.model';
import {ShiftDraft} from '@app/core/models/ShiftDraft.model';
import {Group} from '@app/core/models/group.model';
import {User} from '@app/core/models/user.model';
import {Observable} from 'rxjs';
import {getCurrentState} from '@app/core/helpers';
import {ISalaryPercentageModel} from '@app/core/models/salary-percentage-live.model';
import {ISalaryExpenseStatisticsModel} from '@app/core/models/salary-expense-statistics.model';
import {ESTATISTICSTACKTYPE, ETURNOVERTYPE} from '@app/core/enums';
import {IRevenueStatisticsModel} from '@app/core/models/statistics-revenue.model';

export interface IStatistics {

	date_with_salary_totals_collection: IStatisticsSalaryWithDatesTotalCollection[];

	group_with_salary_totals_collection: {
		group: Group;
		salary_totals_calculation: ISalaryTotalsCalculation;
	}[];
	user_with_salary_totals_collection: {
		user: User;
		salary_totals_calculation: ISalaryTotalsCalculation;
	}[];

	shifts_currently_calculating_count: number;
}

export interface ISalaryTotalsCalculation {
	hour_salaries_to_duration: [{ hour_salary: number, duration: number }];
	total_approved_duration: number;
	total_approved_salary: number;
	total_future_duration: number;
	total_future_salary: number;
	total_approved_count: number;
	total_future_count: number;
	total_approved_salary_with_additional_expenses_percentage_added: number;
	total_future_salary_with_additional_expenses_percentage_added: number;
	shift_with_calculations_collection: {
		future_duration: number;
		future_salary: number;
		approved_duration: number;
		shift: Shift;
	}[];
}

export interface IStatisticsSalaryWithDatesTotalCollection {
	date: string;
	date_range: {
		start: string;
		end: string;
	};
	salary_totals_calculation: ISalaryTotalsCalculation;
	turnover_totals_calculation: ITurnoverStatistics;
}

export interface ITurnoverStatistics {
	total_turnover_expected: number;
	total_turnover_actual: number;
	total_turnover_imported: number;
}

export interface ISalaryExpensesStatisticsFilter {
	departments: { id: number }[];
	groups: { id: number | string }[];
	users: { id: number }[];
	grouping: ESTATISTICSTACKTYPE;
}

export interface IStatisticsRevenueFilter {
	departments: { id: number }[];
}

@Injectable()
export class StatisticService extends BaseService {
	constructor(
		public authService: AuthService,
		public interService: InternationalizationService,
		public httpService: HttpService,
		public router: Router,
		public store: Store<IAppState>,
	) {
		super(authService, interService, httpService, store);
	}

	public fresh(params?): Shift {
		if (!params) {
			return new Shift(this.tenantTZ, this.interService);
		}

		return new Shift(this.tenantTZ, this.interService).deserialize(params);
	}

	public getStatistics(startDate: Moment, endDate: Moment, shiftDraft?: ShiftDraft | undefined | null): Observable<IStatistics> {
		const payload = {
			start_date: startDate.format('YYYY-MM-DD'),
			end_date: endDate.format('YYYY-MM-DD'),
		};
		if (shiftDraft) {
			payload['shift_draft_id'] = shiftDraft.id;
		}

		return this.httpService.getJson<IStatistics>('shifts/statistics', payload);
	}

	public patchTurnover(date: Moment, data: { turnover: number, type: ETURNOVERTYPE }): Observable<any> {
		const formattedDate = new RDate(this.tenantTZ, this.interService)
			.deserialize(date.format('YYYY-MM-DD') + ' 23:59:59', 'localDate', undefined, true);

		return this.httpService.patchJson<IStatistics>('turnovers/' + formattedDate.databaseDate, data);
	}

	public getSalaryPercentage(): Observable<ISalaryPercentageModel[]> {
		return this.httpService.getJson('statistics/department/' + getCurrentState(this.store).session.department.id + '/salary-percentage/last-day');
	}

	public getSalaryExpenseStatistics(from: Moment, to: Moment, filters: ISalaryExpensesStatisticsFilter): Observable<ISalaryExpenseStatisticsModel[]> {
		if (filters.departments?.length === 0) {
			filters.departments = undefined;
		}
		if (filters.groups?.length === 0) {
			filters.groups = undefined;
		}
		if (filters.users?.length === 0) {
			filters.users = undefined;
		}
		if (!filters?.grouping) {
			filters.grouping = undefined;
		}

		return this.httpService.postJson('statistics/salary-expenses', {
			start_date: from?.format('YYYY-MM-DD'),
			end_date: to?.format('YYYY-MM-DD'),
			departments: filters.departments,
			groups: filters.groups,
			grouping: filters.grouping,
			users: filters.users,
		});
	}

	public getRevenue(from: Moment, to: Moment, filters: IStatisticsRevenueFilter): Observable<IRevenueStatisticsModel[]> {
		if (filters.departments?.length === 0) {
			filters.departments = undefined;
		}

		return this.httpService.postJson('statistics/day-turnover', {
			start_date: from.format('YYYY-MM-DD'),
			end_date: to.format('YYYY-MM-DD'),
			departments: filters.departments,
		});
	}
}
